/**
 * Lazy-Loaded Components for Database Operations Page
 *
 * Phase 6: Frontend Performance
 *
 * This file provides lazy-loaded versions of heavy components to reduce
 * initial bundle size and improve load time.
 *
 * Features:
 * - Dynamic imports for code splitting
 * - Loading fallbacks
 * - Error boundaries
 * - Preloading on hover/idle
 */

import { lazy, Suspense, ComponentType } from 'react';
import {
  TablesTabSkeleton,
  PerformanceTabSkeleton,
  CleanupTabSkeleton,
  JobsTabSkeleton,
  CardSkeleton,
} from './LoadingSkeletons';
import { ErrorBoundary } from './ErrorBoundary';

// ============================================
// LAZY LOADED COMPONENTS
// ============================================

/**
 * Heavy Components - Loaded on demand
 */

// Cleanup Wizard (~50KB) - Only loaded when user clicks "Run Cleanup"
export const LazyCleanupWizard = lazy(() =>
  import('./CleanupWizard').then((module) => ({
    default: module.CleanupWizard || module.default,
  }))
);

// Job Detail Modal (~30KB) - Loaded when viewing job details
export const LazyJobDetailModal = lazy(() =>
  import('./JobDetailModal').then((module) => ({
    default: module.default,
  }))
);

// Explain Plan Modal (~25KB) - Loaded when analyzing queries
export const LazyExplainPlanModal = lazy(() =>
  import('./ExplainPlanModal').then((module) => ({
    default: module.ExplainPlanModal || module.default,
  }))
);

// Chart Components (~70KB total for Recharts) - Loaded per tab
export const LazyConnectionPoolTimeline = lazy(() =>
  import('./ConnectionPoolTimeline').then((module) => ({
    default: module.ConnectionPoolTimeline || module.default,
  }))
);

export const LazyQueryPerformanceChart = lazy(() =>
  import('./QueryPerformanceChart').then((module) => ({
    default: module.QueryPerformanceChart || module.default,
  }))
);

// Interactive Tour (~40KB) - Loaded on first visit only
export const LazyInteractiveTour = lazy(() =>
  import('./InteractiveTour').then((module) => ({
    default: module.InteractiveTour,
  }))
);

// ============================================
// WRAPPED COMPONENTS WITH SUSPENSE
// ============================================

/**
 * withLazyLoad
 *
 * HOC that wraps a lazy component with Suspense and ErrorBoundary.
 *
 * Usage:
 *   const LazyComponent = withLazyLoad(
 *     lazy(() => import('./Component')),
 *     <Skeleton />
 *   );
 */
export function withLazyLoad<P extends object>(
  LazyComponent: ComponentType<P>,
  fallback: React.ReactNode = <CardSkeleton />,
  errorFallback?: React.ReactNode
) {
  return function WrappedLazyComponent(props: P) {
    return (
      <ErrorBoundary fallback={errorFallback}>
        <Suspense fallback={fallback}>
          <LazyComponent {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}

/**
 * Pre-wrapped lazy components with appropriate fallbacks
 */

export const CleanupWizard = withLazyLoad(
  LazyCleanupWizard,
  <CleanupTabSkeleton />,
  <div className="error-state">Failed to load cleanup wizard</div>
);

export const JobDetailModal = withLazyLoad(
  LazyJobDetailModal,
  <CardSkeleton />,
  <div className="error-state">Failed to load job details</div>
);

export const ExplainPlanModal = withLazyLoad(
  LazyExplainPlanModal,
  <CardSkeleton />,
  <div className="error-state">Failed to load query plan</div>
);

export const ConnectionPoolTimeline = withLazyLoad(
  LazyConnectionPoolTimeline,
  <div style={{ width: '100%', height: 250 }} className="skeleton" />,
  <div className="error-state">Failed to load chart</div>
);

export const QueryPerformanceChart = withLazyLoad(
  LazyQueryPerformanceChart,
  <div style={{ width: '100%', height: 250 }} className="skeleton" />,
  <div className="error-state">Failed to load chart</div>
);

export const InteractiveTour = withLazyLoad(
  LazyInteractiveTour,
  null, // No fallback for tour
  null // Silent error for tour
);

// ============================================
// PRELOADING UTILITIES
// ============================================

/**
 * Preload a component on hover/focus.
 *
 * Usage:
 *   <button {...preloadOnHover(LazyCleanupWizard)}>
 *     Run Cleanup
 *   </button>
 */
export function preloadOnHover<P>(
  LazyComponent: ComponentType<P>
): {
  onMouseEnter: () => void;
  onFocus: () => void;
} {
  const preload = () => {
    // Trigger the lazy import
    if ('preload' in LazyComponent && typeof LazyComponent.preload === 'function') {
      LazyComponent.preload();
    }
  };

  return {
    onMouseEnter: preload,
    onFocus: preload,
  };
}

/**
 * Preload a component on idle.
 *
 * Usage:
 *   useEffect(() => {
 *     preloadOnIdle(LazyCleanupWizard);
 *   }, []);
 */
export function preloadOnIdle<P>(LazyComponent: ComponentType<P>, timeout = 2000) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        if ('preload' in LazyComponent && typeof LazyComponent.preload === 'function') {
          LazyComponent.preload();
        }
      },
      { timeout }
    );
  } else {
    setTimeout(() => {
      if ('preload' in LazyComponent && typeof LazyComponent.preload === 'function') {
        LazyComponent.preload();
      }
    }, timeout);
  }
}

/**
 * Preload all components.
 *
 * Call this on idle to preload all lazy components.
 */
export function preloadAll() {
  const components = [
    LazyCleanupWizard,
    LazyJobDetailModal,
    LazyExplainPlanModal,
    LazyConnectionPoolTimeline,
    LazyQueryPerformanceChart,
    LazyInteractiveTour,
  ];

  components.forEach((component, index) => {
    // Stagger preloads
    setTimeout(() => {
      if ('preload' in component && typeof component.preload === 'function') {
        component.preload();
      }
    }, index * 500);
  });
}

// ============================================
// ROUTE-LEVEL CODE SPLITTING
// ============================================

/**
 * Lazy-loaded tabs for route-level splitting.
 * Only load the tab when user navigates to it.
 */

export const LazyTablesTab = lazy(() =>
  import('./TablesTab').then((module) => ({
    default: module.TablesTab || module.default,
  }))
);

export const LazyPerformanceTab = lazy(() =>
  import('./PerformanceTab').then((module) => ({
    default: module.PerformanceTab || module.default,
  }))
);

export const LazyCleanupTab = lazy(() =>
  import('./CleanupTab').then((module) => ({
    default: module.CleanupTab || module.default,
  }))
);

export const LazyJobsTab = lazy(() =>
  import('./JobsTab').then((module) => ({
    default: module.default,
  }))
);

// Pre-wrapped with skeletons
export const TablesTab = withLazyLoad(LazyTablesTab, <TablesTabSkeleton />);
export const PerformanceTab = withLazyLoad(LazyPerformanceTab, <PerformanceTabSkeleton />);
export const CleanupTab = withLazyLoad(LazyCleanupTab, <CleanupTabSkeleton />);
export const JobsTab = withLazyLoad(LazyJobsTab, <JobsTabSkeleton />);

// ============================================
// USAGE EXAMPLES
// ============================================

/*
Example 1: Basic lazy loading with Suspense
============================================
import { CleanupWizard } from './LazyComponents';

const CleanupTab = () => {
  const [showWizard, setShowWizard] = useState(false);

  return (
    <div>
      <button onClick={() => setShowWizard(true)}>
        Run Cleanup
      </button>

      {showWizard && (
        <CleanupWizard onClose={() => setShowWizard(false)} />
      )}
    </div>
  );
};


Example 2: Preload on hover
============================
import { preloadOnHover, LazyCleanupWizard } from './LazyComponents';

const CleanupTab = () => {
  return (
    <button
      onClick={handleClick}
      {...preloadOnHover(LazyCleanupWizard)}
    >
      Run Cleanup
    </button>
  );
};


Example 3: Preload on idle
===========================
import { preloadOnIdle, LazyJobDetailModal } from './LazyComponents';

const JobsTab = () => {
  useEffect(() => {
    // Preload modal after 2 seconds of idle time
    preloadOnIdle(LazyJobDetailModal, 2000);
  }, []);

  return <div>...</div>;
};


Example 4: Preload all components
==================================
import { preloadAll } from './LazyComponents';

const DatabasePage = () => {
  useEffect(() => {
    // Preload all lazy components when page is idle
    preloadAll();
  }, []);

  return <div>...</div>;
};


Example 5: Route-level splitting
=================================
import { TablesTab, PerformanceTab } from './LazyComponents';

const DatabasePage = () => {
  const [activeTab, setActiveTab] = useState('tables');

  return (
    <div>
      <TabButtons onChange={setActiveTab} />

      {activeTab === 'tables' && <TablesTab />}
      {activeTab === 'performance' && <PerformanceTab />}
    </div>
  );
};
*/

// ============================================
// BUNDLE SIZE SAVINGS
// ============================================

/*
Estimated bundle size savings:

Component                      Size    When Loaded
-------------------------------------------------
CleanupWizard                  ~50KB   On wizard open
JobDetailModal                 ~30KB   On job detail click
ExplainPlanModal              ~25KB   On explain plan click
Charts (Recharts)             ~70KB   On Performance tab
InteractiveTour               ~40KB   On first visit
-------------------------------------------------
Total potential savings:      ~215KB  From initial bundle

Initial bundle:               ~800KB
After optimization:           ~585KB  (27% reduction)
With compression (gzip):      ~175KB  (78% smaller)
*/
